import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

interface RetryErrorCardProps {
  message?: string;
  onRetry: () => void;
  isRetrying?: boolean;
  retryCount?: number;
  maxRetries?: number;
  className?: string;
  compact?: boolean;
}

export function RetryErrorCard({
  message = 'Erreur lors du chargement',
  onRetry,
  isRetrying = false,
  retryCount = 0,
  maxRetries = 3,
  className,
  compact = false,
}: RetryErrorCardProps) {
  const showAutoRetryMessage = retryCount > 0 && retryCount < maxRetries;

  if (compact) {
    return (
      <div className={cn(
        "flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground",
        className
      )}>
        <AlertTriangle className="h-4 w-4 text-destructive" />
        <span>{message}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}
          disabled={isRetrying}
          className="h-7 px-2"
        >
          {isRetrying ? (
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex flex-col items-center justify-center gap-3 py-8 px-4 text-center",
      className
    )}>
      <div className="p-3 rounded-full bg-destructive/10">
        <AlertTriangle className="h-5 w-5 text-destructive" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{message}</p>
        {showAutoRetryMessage && (
          <p className="text-xs text-muted-foreground">
            Nouvelle tentative ({retryCount}/{maxRetries})...
          </p>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        disabled={isRetrying}
        className="gap-2"
      >
        {isRetrying ? (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            Chargement...
          </>
        ) : (
          <>
            <RefreshCw className="h-4 w-4" />
            Réessayer
          </>
        )}
      </Button>
    </div>
  );
}
